/* Waggle Enrich — motion.js
   Reveals (line mask, image curtain, fade-lift; IntersectionObserver at 15%, once), the hero entrance
   upgrade (per-line mask when the module arrives early; any scroll or key finishes the timeline),
   desktop parallax fallback, the "View" disc, shared-element view-transition names on the clicked card,
   Lenis on desktop fine pointers only (stopped while overlays are open), and the add-to-basket arc.
   Everything checks prefers-reduced-motion; content is never hidden without a CSS failsafe. */

const W = window.Waggle;
const doc = document;
const root = doc.documentElement;
const motionAllowed = () => !W.reducedMotion();
const mq = (query) => window.matchMedia(query);

root.classList.add('motion-ready');

/* Hero film: plays on every width unless motion is reduced. The still photo stays underneath and the
   film only fades in once it is really playing, so a blocked autoplay (iOS Low Power Mode, data saver)
   simply leaves the photo. Retries on the first touch / scroll / key and when the tab becomes visible;
   pauses while off screen or hidden. */
const heroVideo = doc.querySelector('.hero__video');
if (heroVideo) {
  const reduce = mq('(prefers-reduced-motion: reduce)');
  heroVideo.muted = true;
  heroVideo.defaultMuted = true;
  heroVideo.playsInline = true;
  heroVideo.setAttribute('muted', '');
  heroVideo.setAttribute('playsinline', '');
  heroVideo.setAttribute('webkit-playsinline', '');
  let inView = true;
  const retryEvents = ['touchstart', 'pointerdown', 'scroll', 'keydown'];
  const dropRetries = () => retryEvents.forEach((type) => window.removeEventListener(type, retry));
  const play = () => {
    if (reduce.matches || doc.hidden || !inView) return;
    const attempt = heroVideo.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  };
  function retry() {
    play();
  }
  heroVideo.addEventListener('playing', () => {
    heroVideo.classList.add('is-playing');
    dropRetries();
  });
  retryEvents.forEach((type) => window.addEventListener(type, retry, { passive: true }));
  doc.addEventListener('visibilitychange', () => {
    if (doc.hidden) heroVideo.pause();
    else play();
  });
  window.addEventListener('pageshow', play);
  reduce.addEventListener('change', () => {
    if (reduce.matches) {
      heroVideo.pause();
      heroVideo.classList.remove('is-playing');
    } else play();
  });
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView) play();
      else heroVideo.pause();
    }).observe(heroVideo);
  }
  if (heroVideo.readyState >= 3 && !heroVideo.paused) heroVideo.classList.add('is-playing');
  play();
}

/* ---------- Line splitting (client side, after fonts) ---------- */
const fontsReady = () =>
  doc.fonts && doc.fonts.ready ? Promise.race([doc.fonts.ready, W.wait(1000)]) : Promise.resolve();

const splitLines = (el) => {
  if (el.dataset.split) return W.qsa('.line__inner', el);
  el.dataset.split = '1';
  el.__original = el.innerHTML;
  const words = [];
  const walk = (node, chain) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        child.textContent.split(/\s+/).forEach((part) => {
          if (part) words.push({ text: part, chain });
        });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.tagName === 'BR') words.push({ br: true });
        else walk(child, chain.concat(child));
      }
    });
  };
  walk(el, []);
  el.replaceChildren();
  const placed = [];
  words.forEach((word) => {
    if (word.br) {
      el.appendChild(doc.createElement('br'));
      return;
    }
    const span = doc.createElement('span');
    span.textContent = word.text;
    let outer = span;
    for (let i = word.chain.length - 1; i >= 0; i -= 1) {
      const clone = word.chain[i].cloneNode(false);
      clone.appendChild(outer);
      outer = clone;
    }
    el.append(outer, doc.createTextNode(' '));
    placed.push({ span, outer });
  });
  const lines = [];
  let top = null;
  placed.forEach((item) => {
    const y = item.span.getBoundingClientRect().top;
    if (top === null || Math.abs(y - top) > 3) {
      lines.push([]);
      top = y;
    }
    lines[lines.length - 1].push(item.outer);
  });
  el.replaceChildren();
  return lines.map((line) => {
    const wrap = doc.createElement('span');
    wrap.className = 'line';
    const inner = doc.createElement('span');
    inner.className = 'line__inner';
    line.forEach((node, index) => {
      inner.appendChild(node);
      if (index < line.length - 1) inner.appendChild(doc.createTextNode(' '));
    });
    wrap.appendChild(inner);
    el.appendChild(wrap);
    return inner;
  });
};

const unsplit = (el) => {
  if (!el.dataset.split || el.__original === undefined) return;
  el.innerHTML = el.__original;
  delete el.dataset.split;
  delete el.__original;
};

/* ---------- Reveals ---------- */
const reveal = async (el, delay) => {
  const type = el.dataset.reveal;
  if (type === 'lines') {
    await fontsReady();
    const inners = splitLines(el);
    el.classList.add('is-revealed');
    let last = null;
    inners.forEach((inner, index) => {
      last = inner.animate([{ transform: 'translateY(105%)' }, { transform: 'translateY(0)' }], {
        duration: 1000,
        delay: delay + index * 80,
        easing: W.ease.out,
        fill: 'backwards',
      });
    });
    W.finished(last).then(() => unsplit(el));
    return;
  }
  el.classList.add('is-revealed');
  if (type === 'curtain') {
    el.animate([{ clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)' }], {
      duration: 1200,
      delay,
      easing: W.ease.inOut,
      fill: 'backwards',
    });
    const img = el.querySelector('img');
    if (img) {
      img.animate([{ transform: 'scale(1.04)' }, { transform: 'scale(1)' }], {
        duration: 1600,
        delay,
        easing: W.ease.out,
        fill: 'backwards',
      });
    }
    return;
  }
  el.animate(
    [
      { opacity: 0, transform: 'translateY(12px)' },
      { opacity: 1, transform: 'translateY(0)' },
    ],
    { duration: 900, delay, easing: W.ease.out, fill: 'backwards' }
  );
};

const io =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          const batch = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
          batch.sort((a, b) => {
            const ra = a.getBoundingClientRect();
            const rb = b.getBoundingClientRect();
            return Math.round(ra.top - rb.top) || ra.left - rb.left;
          });
          batch.forEach((el, index) => {
            io.unobserve(el);
            reveal(el, Math.min(index, 5) * 90);
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      )
    : null;

const observe = (scope = doc) => {
  W.qsa('[data-reveal]:not(.is-revealed)', scope).forEach((el) => {
    if (el.dataset.revealBound) return;
    el.dataset.revealBound = '1';
    if (!motionAllowed() || !io) el.classList.add('is-revealed');
    else io.observe(el);
  });
};
observe();

/* ---------- Hero entrance (brief §7.4) ---------- */
const hero = W.qs('[data-hero]');
const head = W.qs('[data-site-head]');
if (hero && motionAllowed()) {
  const title = W.qs('[data-hero-title]', hero);
  const elapsed = performance.now();
  let heroLines = [];
  if (title && elapsed < 480) {
    title.style.animation = 'none';
    heroLines = splitLines(title);
    let last = null;
    heroLines.forEach((inner, index) => {
      last = inner.animate([{ transform: 'translateY(105%)' }, { transform: 'translateY(0)' }], {
        duration: 900,
        delay: Math.max(0, 520 + index * 90 - elapsed),
        easing: W.ease.out,
        fill: 'backwards',
      });
    });
    W.finished(last).then(() => unsplit(title));
  }
  const events = ['wheel', 'touchstart', 'keydown', 'scroll'];
  const settle = () => {
    hero.classList.add('is-settled');
    if (head) head.classList.add('is-settled');
    heroLines.forEach((inner) => inner.getAnimations().forEach((animation) => animation.finish()));
    events.forEach((type) => window.removeEventListener(type, settle));
  };
  events.forEach((type) => window.addEventListener(type, settle, { passive: true }));
  setTimeout(() => events.forEach((type) => window.removeEventListener(type, settle)), 1900);
}

/* ---------- Parallax fallback (CSS scroll-driven animations do it where supported) ---------- */
const parallaxTargets = W.qsa('[data-parallax]');
const scrollTimelines = window.CSS && CSS.supports('animation-timeline: view()');
if (
  parallaxTargets.length &&
  !scrollTimelines &&
  motionAllowed() &&
  mq('(pointer: fine) and (min-width: 990px)').matches
) {
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    parallaxTargets.forEach((el) => {
      const box = el.parentElement.getBoundingClientRect();
      if (box.bottom < 0 || box.top > vh) return;
      if (el.dataset.parallax === 'hero') {
        const progress = Math.min(1, Math.max(0, window.scrollY / vh));
        el.style.transform = `translate3d(0, ${(progress * 8).toFixed(2)}%, 0)`;
      } else {
        const progress = (vh - box.top) / (vh + box.height);
        el.style.transform = `translate3d(0, ${((progress - 0.5) * 8).toFixed(2)}%, 0)`;
      }
    });
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  update();
}

/* ---------- "View" disc (pointer: fine only, decorative) ---------- */
const disc = W.qs('.view-disc');
if (disc && W.settings.viewDisc && motionAllowed() && W.finePointer()) {
  let x = -200;
  let y = -200;
  let tx = x;
  let ty = y;
  let on = false;
  let frame = null;
  const tick = () => {
    x += (tx - x) * 0.18;
    y += (ty - y) * 0.18;
    disc.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
    frame = Math.abs(tx - x) > 0.2 || Math.abs(ty - y) > 0.2 ? requestAnimationFrame(tick) : null;
  };
  doc.addEventListener(
    'pointermove',
    (event) => {
      tx = event.clientX;
      ty = event.clientY;
      if (!frame) frame = requestAnimationFrame(tick);
    },
    { passive: true }
  );
  doc.addEventListener('pointerover', (event) => {
    const zone = event.target.closest && event.target.closest('[data-view-disc]');
    const next = Boolean(zone) && !W.overlays.isOpen();
    if (next && !on) {
      x = tx = event.clientX;
      y = ty = event.clientY;
      disc.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    }
    if (next !== on) {
      on = next;
      disc.classList.toggle('is-on', on);
    }
  });
  doc.addEventListener('overlay:open', () => {
    on = false;
    disc.classList.remove('is-on');
  });
}

/* ---------- View transitions: name only the clicked card ---------- */
const clearNames = () =>
  W.qsa('[data-vt-named]').forEach((el) => {
    el.style.viewTransitionName = '';
    el.removeAttribute('data-vt-named');
  });
if (W.settings.viewTransitions && motionAllowed()) {
  doc.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    const card = link && link.closest('[data-card]');
    if (!card) return;
    clearNames();
    const id = card.dataset.productId;
    const media = card.querySelector('[data-vt-media] .plate');
    const title = card.querySelector('[data-vt-title]');
    if (media) {
      media.style.viewTransitionName = `product-media-${id}`;
      media.setAttribute('data-vt-named', '');
    }
    if (title) {
      title.style.viewTransitionName = `product-title-${id}`;
      title.setAttribute('data-vt-named', '');
    }
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) clearNames();
  });
}

/* ---------- Lenis: desktop, fine pointer, motion allowed (brief §7.6) ---------- */
if (W.settings.lenis && W.assets && W.assets.lenis && mq('(pointer: fine) and (prefers-reduced-motion: no-preference) and (min-width: 990px)').matches) {
  const script = doc.createElement('script');
  script.src = W.assets.lenis;
  script.defer = true;
  script.onload = () => {
    if (typeof window.Lenis !== 'function') return;
    const lenis = new window.Lenis({
      lerp: 0.1,
      wheelMultiplier: 1,
      syncTouch: false,
      autoRaf: true,
      anchors: { offset: -96 },
      stopInertiaOnNavigate: true,
      prevent: (node) => Boolean(node && node.closest && node.closest('dialog, [data-lenis-prevent], .shop-panel')),
    });
    W.lenis = lenis;
    if (W.overlays.isOpen()) lenis.stop();
    doc.addEventListener('overlay:open', () => lenis.stop());
    doc.addEventListener('overlay:close', () => {
      if (!W.overlays.isOpen()) lenis.start();
    });
  };
  doc.head.appendChild(script);
}

/* ---------- Add to basket: arc hand-off (brief §8.1.5) ---------- */
const arc = async (fromEl, imageSrc) => {
  if (!motionAllowed() || !W.finePointer() || !fromEl) return;
  W.header.show();
  const target = doc.querySelector('#HeaderCart [data-cart-count]') || doc.getElementById('HeaderCart');
  if (!target) return;
  await W.wait(40);
  const start = fromEl.getBoundingClientRect();
  const end = target.getBoundingClientRect();
  const sx = start.left + start.width / 2 - 24;
  const sy = start.top + start.height / 2 - 24;
  const ex = end.left + end.width / 2 - 24;
  const ey = end.top + end.height / 2 - 24;
  const ghost = doc.createElement('div');
  ghost.className = 'arc-ghost';
  ghost.setAttribute('aria-hidden', 'true');
  const inner = doc.createElement('span');
  inner.className = 'arc-ghost__inner';
  if (imageSrc) {
    const img = doc.createElement('img');
    img.src = imageSrc;
    img.alt = '';
    img.width = 48;
    img.height = 48;
    inner.appendChild(img);
  }
  ghost.appendChild(inner);
  doc.body.appendChild(ghost);
  const xMove = ghost.animate([{ transform: `translate3d(${sx}px, 0, 0)` }, { transform: `translate3d(${ex}px, 0, 0)` }], {
    duration: 620,
    easing: W.ease.inOut,
    fill: 'forwards',
  });
  inner.animate(
    [
      { transform: `translate3d(0, ${sy}px, 0) scale(1)`, opacity: 1 },
      { transform: `translate3d(0, ${ey}px, 0) scale(0.45)`, opacity: 0.85 },
    ],
    { duration: 620, easing: W.ease.out, fill: 'forwards' }
  );
  await W.finished(xMove);
  ghost.remove();
};

W.motion = { observe, arc, splitLines, unsplit };
